import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from "react-intl";
import { inject, observer } from "mobx-react";
import styled from 'styled-components';
import { Box, Typography, IconButton, Stack } from '@mui/material';
import { Icon } from '@iconify/react';
import PoKoRoIcon from '@/common/Images/PoKoRoIcon.svg';
import { useToast } from '../../../components/useToast';
import { isAgreePush } from "@/nativebridge/JsToNative";
import {COMMON_UTIL} from "@/common/util/common.util";

const PopupOverlay = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const PopupContent = styled(Stack)`
  gap: 20px;
  background-color: #fff;
  padding: 32px 24px;
  border-radius: 20px;
  position: relative;
  max-width: 400px;
  width: 90vw;
  box-sizing: border-box;
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  top: 0;
  right: 0;
`;

const BirdBox = styled(Box)`
  display: flex;
  align-items: center;
  background: #FFF9E1;
  border-radius: 8px;
  padding: 16px;
`;

const BirdImg = styled.img`
  width: 36px;
  height: 36px;
  margin-right: 12px;
`;

const InfoList = styled.ul`
  padding-left: 18px;
  color: #8f8f8f;
  font-size: 14px;
  list-style: disc;
  li {
    margin-bottom: 8px;
  }
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 16px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  background: #FDD751;
  color: #222;
  &:nth-child(1) {
    background: #fff;
    border: 1px solid #8f8f8f;
  }
  &:hover {
    opacity: 0.8;
  }
`;

function PushPopup(props) {
  const { authStore, userStore } = props;
  const { isOpen, onClose } = props;
  const { intl } = props;
  const { showDeleteToast } = useToast();
  const [agreementDate, setAgreementDate] = useState(null);

  useEffect(() => {
    const now = new Date();
    const formattedDate = `${now.getFullYear()}. ${String(now.getMonth() + 1).padStart(2, '0')}. ${String(now.getDate()).padStart(2, '0')}`;
    setAgreementDate(formattedDate);
  }, []);

  if (!isOpen) return null;

  const handleButtonClick = (action) => {
    if (action === 'accept') {
      userStore.updateUserPushAgreement(authStore.loginUser.id, true, () => {
        if (COMMON_UTIL.isWebView()) {
          isAgreePush(userStore.userPushAgreement.agreed ? 1 : 0);
        }
      });
      showDeleteToast(
        <span style={{ whiteSpace: "pre-line" }}>
          {intl.formatMessage({ id: "Toast_marketing_notification_on" })} {/* 푸시 알림을 동의했습니다. */}
          &nbsp;{agreementDate}
        </span>
      );
    } else {
      userStore.updateUserPushAgreement(authStore.loginUser.id, false, () => {
        if (COMMON_UTIL.isWebView()) {
          isAgreePush(userStore.userPushAgreement.agreed ? 1 : 0);
        }
      });
      showDeleteToast(
        <span style={{ whiteSpace: "pre-line" }}>
          {intl.formatMessage({ id: "Toast_marketing_notification_off" })} {/* 푸시 알림을 거부했습니다. */}
          &nbsp;{agreementDate}
        </span>
      );
    }
    onClose();
  };

  return (
    <PopupOverlay onClick={onClose}>
      <PopupContent onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose} aria-label="닫기">
          <Icon icon="mdi:close" />
        </CloseButton>
        <Typography variant="h6" align="center" fontWeight={700}>
          {intl.formatMessage({ id: "EventPopup_title" })} {/* 푸시 알림 설정 */}
        </Typography>
        <BirdBox>
          <BirdImg src={PoKoRoIcon} alt="포코로 새 일러스트" />
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {intl.formatMessage({ id: "EventPopup_description" })} {/* 포코로의 다양한 혜택 및 소식을 받아보세요. */}
            </Typography>
          </Box>
        </BirdBox>
        <InfoList>
          <li>{intl.formatMessage({ id: "EventPopup_description2" })}</li> {/* 푸시알림 설정은 설정 > 기타 > 푸시 알림 설정에서 변경이 가능합니다. */}
          <li>{intl.formatMessage({ id: "EventPopup_description3" })}</li> {/* 기기 알람 설정을 위해 알림 권한 팝업이 한번 더 요청됩니다. */}
        </InfoList>
        <Stack flexDirection="row" gap={1}>
          <ActionButton onClick={() => handleButtonClick('reject')}>{intl.formatMessage({ id: "EventPopup_button_left" })}</ActionButton> {/* 거부하기 */}
          <ActionButton onClick={() => handleButtonClick('accept')}>{intl.formatMessage({ id: "EventPopup_button_right" })}</ActionButton> {/* 알림 받기 */}
        </Stack>
      </PopupContent>
    </PopupOverlay>
  );
}

PushPopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default injectIntl(
  inject("authStore", "userStore")(observer(PushPopup))
);